import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button, Card, CardContent, Chip, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api, { getInfluencers } from '../services/api';
import { toast } from 'react-toastify';

function InfluencerList({ campaignId, user }) {
  const [influencers, setInfluencers] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(user?.role === 'imt_team' ? 'all' : 'pending');
  const [selectedRows, setSelectedRows] = useState([]);


  useEffect(() => {
    fetchInfluencers();
    if (campaignId) fetchCampaign();
  }, [campaignId]);

  const fetchInfluencers = async () => {
    setLoading(true);
    try {
      const response = await getInfluencers();
      const all = response.data || [];
      setInfluencers(all.filter(inf => inf.negotiation_status === 1));
    } catch (error) {
      console.error('Failed to fetch influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaign = async () => {
    try {
      const response = await api.get(`/campaign/${campaignId}`);
      setCampaign(response.data?.data || null);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRows.length || !campaignId) return;
    try {
      await api.put(`/campaign/${campaignId}/influencer-pending-list`, { influencerIds: selectedRows });
      toast.success(`${selectedRows.length} influencer(s) added to pending list`);
      setSelectedRows([]);
      fetchInfluencers();
      fetchCampaign();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit influencers');
    }
  };

  const handleApprove = async () => {
    if (!selectedRows.length || !campaignId) return;
    try {
      await api.put(`/campaign/${campaignId}/influencer-approval-list`, { influencerIds: selectedRows });
      toast.success(`${selectedRows.length} influencer(s) approved successfully`);
      setSelectedRows([]);
      fetchCampaign();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve influencers');
    }
  };

  const handleRevert = async () => {
    if (!selectedRows.length || !campaignId) return;
    try {
      await api.put(`/campaign/${campaignId}/influencer-reverted-list`, { influencerIds: selectedRows });
      toast.success(`${selectedRows.length} influencer(s) reverted successfully`);
      setSelectedRows([]);
      fetchCampaign();
    } catch (error) {
      console.error('Revert error:', error);
      toast.error('Failed to revert influencers');
    }
  };


  const getChipColor = (type) => {
    switch (type) {
      case 'Micro': return 'primary';
      case 'Macro': return 'secondary';
      case 'Mid-tier': return 'success';
      case 'Mega': return 'warning';
      case 'Celeb': return 'error';
      default: return 'default';
    }
  };

  const columns = [
    { field: 'sno', headerName: 'SNO', width: 80 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'onboarderName', headerName: 'Onboarder', width: 150 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'city', headerName: 'City', width: 150 },
    { field: 'category', headerName: 'Category', width: 150 },
    { field: 'socialMediaPlatforms', headerName: 'Platform Type', width: 180 },
    { field: 'number', headerName: 'Number', width: 180 },
    { field: 'address', headerName: 'Address', width: 180 },
    {
      field: 'instagramInfluencerType', headerName: 'Instagram Type', width: 180,
      renderCell: (params) => {
        const type = params.row.allData?.instagramInfluencerType;
        if (!type || type === '-') return '-';
        return <Chip label={type} color={getChipColor(type)} size="small" />;
      }
    },
    {
      field: 'totalCostInstagram', headerName: 'Total Cost (Instagram)', width: 180, cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const total = (params.row.allData?.reelCost || 0) + (params.row.allData?.storyCost || 0);
        return total > 0 ? `₹${total.toLocaleString()}` : '-';
      }
    },
    {
      field: 'negotiationCostInstagram', headerName: 'Negotiation Total Cost (Instagram)', width: 220, cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const cost = params.row.allData?.negotiationCost?.reelCost || 0;
        return cost > 0 ? `₹${cost.toLocaleString()}` : '-';
      }
    },
  ];

  // Get influencerPendingIds from campaign.influencerList
  const pendingIds = (campaign?.influencerList || []).map(item => item.influencerPendingId?.toString?.() || item.influencerPendingId).filter(Boolean);
  const approvedIds = (campaign?.influencerList || []).filter(item => item.influencerApprovalId).map(item => item.influencerApprovalId?.toString?.() || item.influencerApprovalId);
  const revertedIds = (campaign?.influencerList || []).filter(item => item.influencerRevertedId).map(item => item.influencerRevertedId?.toString?.() || item.influencerRevertedId);

  const getTabData = (tab) => {
    if (tab === 'all') {
      // imt_team only: all influencers NOT yet in influencerPendingId
      return influencers.filter(inf => !pendingIds.includes(inf._id));
    }
    if (tab === 'pending') {
      // Both roles: influencers in influencerPendingId and not yet approved/reverted
      return influencers.filter(inf => pendingIds.includes(inf._id) && !approvedIds.includes(inf._id) && !revertedIds.includes(inf._id));
    }
    if (tab === 'approved') {
      return influencers.filter(inf => approvedIds.includes(inf._id));
    }
    if (tab === 'rejected') {
      return influencers.filter(inf => revertedIds.includes(inf._id));
    }
    return [];
  };

  const getTabCount = (tab) => getTabData(tab).length;

  const rows = getTabData(activeTab).map((inf, index) => ({
    id: inf._id,
    _id: inf._id,
    sno: index + 1,
    name: inf.name,
    onboarderName: inf.onboarderId?.name || 'N/A',
    state: inf.state,
    city: inf.city,
    category: inf.category,
    socialMediaPlatforms: inf.socialMediaPlatforms,
    number: inf.contactNumber,
    address: inf.address,
    panNumber: inf.panNumber,
    aadharNumber: inf.aadharNumber,
    bankAccountNumber: inf.bankAccountNumber,
    ifscCode: inf.ifscCode,
    holderName: inf.holderName,
    bankName: inf.bankName,
    last10ReelsViews: inf.last10ReelsViews,
    last10ReelsReach: inf.last10ReelsReach,
    instagramLikes: inf.instagramLikes,
    instagramComments: inf.instagramComments,
    instagramSaves: inf.instagramSaves,
    instagramShares: inf.instagramShares,
    instagramReposts: inf.instagramReposts,
    last10ShortsViews: inf.last10ShortsViews,
    last10ShortsReach: inf.last10ShortsReach,
    youtubeLikes: inf.youtubeLikes,
    youtubeComments: inf.youtubeComments,
    youtubeSaves: inf.youtubeSaves,
    youtubeShares: inf.youtubeShares,
    updatedAt: inf.updatedAt,
    allData: inf
  }));

  // Expected Influencer: sum of numberOfCreators from campaign.dmTeam.creators
  const expectedInfluencer = campaign?.dmTeam?.creators?.reduce((sum, c) => sum + (parseInt(c.numberOfCreators) || 0), 0) || 0;

  // Budget: from campaign.brandTeam.budget
  const allocatedBudget = campaign?.brandTeam?.budget || 0;

  // Actual Budget: role-based calculation
  // imt_team: sum of selected rows' (reelCost + storyCost)
  // dm_team: sum of active tab's data (reelCost + storyCost)
  const actualBudget = user?.role === 'imt_team'
    ? selectedRows.reduce((sum, id) => {
        const inf = influencers.find(i => i._id === id);
        if (!inf) return sum;
        return sum + (inf.reelCost || 0) + (inf.storyCost || 0);
      }, 0)
    : getTabData(activeTab).reduce((sum, inf) => sum + (inf.reelCost || 0) + (inf.storyCost || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            Influencer List
          </Typography>
          <Tabs
            value={activeTab}
            onChange={(e, val) => { setActiveTab(val); setSelectedRows([]); }}
            sx={{ mb: 0, '& .MuiTab-root': { fontWeight: 600 }, '& .Mui-selected': { color: '#DAFF7C' }, '& .MuiTabs-indicator': { backgroundColor: '#DAFF7C' } }}
          >
            {user?.role === 'imt_team' && <Tab label={`All Influencers (${getTabCount('all')})`} value="all" />}
            <Tab label={`Pending (${getTabCount('pending')})`} value="pending" />
            <Tab label={`Approved (${getTabCount('approved')})`} value="approved" />
            <Tab label={`Reverted (${getTabCount('rejected')})`} value="rejected" />
          </Tabs>

          {/* Summary: imt_team only on 'all' tab, dm_team on all tabs */}
          {(user?.role === 'dm_team' || (user?.role === 'imt_team' && activeTab === 'all')) && (
            <Box sx={{ display: 'flex', gap: 4, py: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">Expected Influencer: <strong style={{ color: '#1565c0' }}>{expectedInfluencer}</strong></Typography>
              <Typography variant="body2" color="text.secondary">Allocated Budget: <strong style={{ color: '#2e7d32' }}>₹{allocatedBudget.toLocaleString()}</strong></Typography>
              <Typography variant="body2" color="text.secondary">Actual Budget: <strong style={{ color: '#e65100' }}>₹{actualBudget.toLocaleString()}</strong></Typography>
            </Box>
          )}
          <DataGrid
            columns={columns}
            rows={loading ? [] : rows}
            loading={loading}
            checkboxSelection={activeTab === 'all' || (activeTab === 'pending' && user?.role === 'dm_team')}
            onSelectionModelChange={(ids) => setSelectedRows(ids)}
            selectionModel={selectedRows}
            pageSize={10}
            autoHeight
            sx={{
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f9fa' },
              '& .instagram-cell': { backgroundColor: '#e8f4fcb7 !important' },
              '& .youtube-cell': { backgroundColor: '#fff5e69a !important' }
            }}
          />

          {activeTab === 'all' && selectedRows.length > 0 && user?.role === 'imt_team' && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
              >
                Submit ({selectedRows.length})
              </Button>
            </Box>
          )}

          {activeTab === 'pending' && selectedRows.length > 0 && user?.role === 'dm_team' && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleApprove}
              >
                Approve ({selectedRows.length})
              </Button>
              <Button
                variant="contained"
                onClick={handleRevert}
                sx={{ color: '#666', borderColor: '#ddd' }}
              >
                Revert ({selectedRows.length})
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default InfluencerList;
